// src/components/principal/ParentInvite.jsx (v6 – fully config-driven)

import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { API_PATHS, getParentSignupUrl } from "../../config";

/**
 * Principal dashboard panel for managing the parent invitation system.
 *
 * Shows the current invite link (auto-generated on first use), allows regeneration,
 * displays expiry, enables copying, and lists all registered parent accounts.
 *
 * @component
 * @param {Object} props
 * @param {function} [props.onError] - Callback for error messages (e.g., toast notification)
 * @param {boolean} [props.loading=false] - External loading state to disable buttons
 * @returns {JSX.Element}
 */
const ParentInvite = ({ onError, loading: loadingProp = false }) => {
  const [inviteCode, setInviteCode] = useState("");
  const [inviteUrl, setInviteUrl] = useState(""); // backend-provided full URL if available
  const [inviteMeta, setInviteMeta] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(false);

  const [parentAccounts, setParentAccounts] = useState([]);
  const [parentsLoading, setParentsLoading] = useState(false);

  // Prefer backend-provided URL; fallback to client-generated
  const effectiveInviteUrl =
    inviteUrl || (inviteCode ? getParentSignupUrl(inviteCode) : "");

  const hasInvite = !!inviteCode;

  // Load existing invite
  const loadInvite = async () => {
    setLoadingInvite(true);
    try {
      const { data } = await api.get(API_PATHS.MY_PARENT_INVITE);
      if (!data) return;

      setInviteCode(data.invite_code || "");
      setInviteUrl(data.signup_url || ""); // use if backend provides full URL
      setInviteMeta({
        expires_at: data.expires_at || null,
        expires_in_days:
          typeof data.expires_in_days === "number" ? data.expires_in_days : null,
      });
    } catch (e) {
      // 404 expected when no invite exists yet
      console.error("Failed to load parent invite:", e);
    } finally {
      setLoadingInvite(false);
    }
  };

  // Generate or regenerate invite
  const generate = async () => {
    setLoadingInvite(true);
    try {
      const { data } = await api.post(API_PATHS.GENERATE_PARENT_INVITE, {});
      setInviteCode(data.invite_code || "");
      setInviteUrl(data.signup_url || "");
      setInviteMeta({
        expires_at: data.expires_at || null,
        expires_in_days:
          typeof data.expires_in_days === "number" ? data.expires_in_days : null,
      });
    } catch (e) {
      console.error("Generate invite failed:", e);
      onError?.("Failed to generate invite code. Please try again.");
    } finally {
      setLoadingInvite(false);
    }
  };

  const copyLink = () => {
    if (!effectiveInviteUrl) {
      onError?.("There is no invite link to copy yet.");
      return;
    }
    navigator.clipboard
      .writeText(effectiveInviteUrl)
      .catch(() => onError?.("Failed to copy invite link to clipboard."));
  };

  const fetchParents = async () => {
    setParentsLoading(true);
    try {
      const { data } = await api.get(API_PATHS.PARENT_ACCOUNTS);
      setParentAccounts(data || []);
    } catch (e) {
      console.error("Failed to fetch parent accounts:", e);
      onError?.("Failed to load registered parent accounts.");
    } finally {
      setParentsLoading(false);
    }
  };

  useEffect(() => {
    loadInvite();
    fetchParents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="principal-panel parents-content">
      <h3>Parent Invite Code</h3>

      <p className="text-muted" style={{ marginBottom: "0.75rem" }}>
        The unique private sign-up link for Parents is below. You can copy it and
        share it with parents when you&apos;re ready.
      </p>

      {!hasInvite && (
        <button
          onClick={generate}
          disabled={loadingInvite || loadingProp}
          className="btn btn-primary"
        >
          {loadingInvite || loadingProp
            ? "Working…"
            : "Generate Invite Code for Parents"}
        </button>
      )}

      {hasInvite && (
        <div className="invite-code-display" style={{ marginTop: "1rem" }}>
          <p style={{ marginBottom: "0.75rem" }}>
            <strong>Link for Parents:</strong>{" "}
            <a
              href={effectiveInviteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="template-download-link"
            >
              {effectiveInviteUrl}
            </a>
            <button
              onClick={copyLink}
              className="btn btn-outline copy-button"
              style={{ marginLeft: "0.75rem" }}
            >
              Copy Link
            </button>
            {inviteMeta?.expires_in_days !== null && (
              <span className="invite-expiry-inline" style={{ marginLeft: 8 }}>
                (Link expires in{" "}
                <strong>
                  {inviteMeta.expires_in_days === 0
                    ? "less than 1 day"
                    : `${inviteMeta.expires_in_days} day${
                      inviteMeta.expires_in_days === 1 ? "" : "s"
                    }`}
                </strong>
                )
              </span>
            )}
          </p>

          <p className="text-muted" style={{ marginTop: "0.75rem", marginBottom: "0.5rem" }}>
            If you ever need to generate a new one, you can click below:
          </p>
          <button
            onClick={generate}
            disabled={loadingInvite || loadingProp}
            className="btn btn-primary"
          >
            {loadingInvite || loadingProp
              ? "Working…"
              : "Generate a New Invite Code"}
          </button>

          <div className="parent-invite-helper" style={{ marginTop: "1.5rem" }}>
            <h4>How to use this with parents</h4>
            <ul>
              <li>
                Paste the link into your{" "}
                <strong>school newsletter, email, or text message</strong> to parents.
              </li>
              <li>
                Parents click the link and are brought to the{" "}
                <strong>Parent Sign-Up</strong> page for your school.
              </li>
              <li>
                They create their own <strong>Parent account</strong> using their email and a password.
              </li>
              <li>
                Once registered, they can log in to the{" "}
                <strong>GhrianGhraffy website</strong> (or use the{" "}
                <strong>GhrianGhraffy App</strong>) and view{" "}
                <strong>approved photos</strong> from your school.
              </li>
            </ul>

            <p style={{ marginTop: "0.75rem" }}>
              <strong>Recommendation:</strong> For the best first impression, try to upload at least{" "}
              <strong>around 50 photos</strong> before sharing this link.
            </p>
          </div>
        </div>
      )}

      <hr style={{ margin: "1.75rem 0", borderColor: "var(--border-subtle)" }} />

      <h3>Registered Parent Accounts</h3>

      {parentsLoading ? (
        <p>Loading parent accounts…</p>
      ) : parentAccounts.length === 0 ? (
        <p>No parent accounts have been created yet.</p>
      ) : (
        <div className="parent-accounts-wrapper" style={{ marginTop: "0.75rem" }}>
          <ul className="parent-accounts-list">
            {parentAccounts.map((p, idx) => (
              <li key={p.email || idx}>
                <strong>{p.email}</strong>
                {p.display_name ? ` — ${p.display_name}` : ""}
                {p.created_at_iso && (
                  <span className="parent-created-at">
                    {" "} (joined {new Date(p.created_at_iso).toLocaleDateString()})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={fetchParents}
        disabled={parentsLoading}
        className="btn btn-outline refresh-parents-button"
        style={{ marginTop: "1rem" }}
      >
        {parentsLoading ? "Refreshing…" : "Refresh List"}
      </button>
    </div>
  );
};

export default ParentInvite;